import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import { RankingEventsTable } from "@/components/tables/ranking-events";
import type { rankingPageFragment$key } from "./__generated__/rankingPageFragment.graphql";
import type { rankingQuery } from "./__generated__/rankingQuery.graphql";
import type { rankingTableQuery } from "./__generated__/rankingTableQuery.graphql";
import { DisabledScreenAlert } from "@/components/disabled-screen-alert";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/ranking",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const [isPending, startTransition] = useTransition();

	const data = useLazyLoadQuery<rankingQuery>(
		graphql`
			query rankingQuery($slug: String!) {
				club(slug: $slug) {
					id
					leaderboard: configuration(key: "LEADERBOARD")
					...rankingPageFragment
				}
			}
		`,
		{
			slug: club?.slug,
		},
	);

	const [ranking, refetch] = useRefetchableFragment<
		rankingTableQuery,
		rankingPageFragment$key
	>(
		graphql`
			fragment rankingPageFragment on Club @refetchable(
				queryName: "rankingTableQuery"
			) {
				ranking {
					events {
						name
						event
						points
						enabled
					}
				}
			}
		`,
		data.club,
	);

	const handleSuccess = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	const isDisabled =
		data.club.leaderboard === "DISABLED" || data.club.leaderboard === "0";

	return (
		<div className="p-6 flex flex-col gap-6">
			{isDisabled && (
				<DisabledScreenAlert
					clubId={data.club.id}
					configKey="LEADERBOARD"
					featureName="Ranking"
				/>
			)}
			<RankingEventsTable
				data={(ranking?.ranking?.events.filter(Boolean) || []).sort((a, b) =>
					a.name.localeCompare(b.name),
				)}
				isPending={isPending}
				clubSlug={club?.slug || ""}
				onSuccess={handleSuccess}
			/>
		</div>
	);
}
